import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { fullJitterRetryDelay } from '~/utils/jitter';

import { archiveRun, unarchiveRun } from '../services/run-detail.service';
import type { ArchiveRefusal } from '../types/run-detail.api.types';
import { controlErrorMessage, isNetworkFailure } from '../utils/run-control-errors';

/** Which way the archive toggle goes. */
export type RunArchiveKind = 'archive' | 'unarchive';

/** Resend budget for a dropped connection only — same, idempotent path-only request. */
const NETWORK_RETRIES = 2;

/** `ArchiveRefusal` values the contract documents for a single-run 409. */
const KNOWN_REFUSALS = new Set<ArchiveRefusal>(['RUN_IN_FLIGHT', 'RUN_DELIVERING']);

/**
 * Archive / unarchive the single run from its header. The endpoint takes no
 * body (path id only) and is a no-op on repeat; a run still in flight or
 * delivering refuses with `RUN_IN_FLIGHT` / `RUN_DELIVERING` — the contract
 * documents this as a 409, so that is the path this toasts, but a defensive
 * check also covers a 200 `ArchiveResult` whose own `results[]` reports
 * `REFUSED_LIVE`, since the shared `ArchiveResult` schema allows it. Either
 * way the run + runs-list caches refresh so the header and any list agree.
 */
export function useRunArchive(runId: string) {
  const { t } = useTranslation('catalog-v2-run');
  const queryClient = useQueryClient();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (kind: RunArchiveKind) =>
      kind === 'archive' ? archiveRun(runId) : unarchiveRun(runId),
    retry: (failureCount, err) => failureCount < NETWORK_RETRIES && isNetworkFailure(err),
    retryDelay: fullJitterRetryDelay,
    onSuccess: (result, kind) => {
      const outcome = result.results[0]?.outcome;
      if (outcome === 'REFUSED_LIVE') {
        const reason = result.results[0]?.reason;
        toast.error(t('header.archiveToast.refused'), {
          description:
            reason && KNOWN_REFUSALS.has(reason)
              ? t(`header.archiveToast.refusedReason.${reason}`)
              : t('header.archiveToast.refusedReason.unknown'),
        });
        return;
      }
      if (result.changed.length > 0) toast.success(t(`header.archiveToast.${kind}Applied`));
      else toast.info(t(`header.archiveToast.${kind}NotApplied`));
    },
    onError: (err) => {
      const refusal =
        err instanceof ApiError &&
        err.serverCode &&
        KNOWN_REFUSALS.has(err.serverCode as ArchiveRefusal)
          ? (err.serverCode as ArchiveRefusal)
          : null;
      if (refusal) {
        toast.error(t('header.archiveToast.refused'), {
          description: t(`header.archiveToast.refusedReason.${refusal}`),
        });
        return;
      }
      toast.error(t('header.toast.failed'), {
        description: controlErrorMessage(err, t('header.toast.failedBody')),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.run(runId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.runs.all() });
    },
  });

  const run = useCallback(
    (kind: RunArchiveKind, options?: { onSettled?: () => void }) => {
      if (isPending) return;
      mutate(kind, { onSettled: () => options?.onSettled?.() });
    },
    [isPending, mutate],
  );

  return {
    archive: (options?: { onSettled?: () => void }) => {
      run('archive', options);
    },
    unarchive: (options?: { onSettled?: () => void }) => {
      run('unarchive', options);
    },
    isPending,
    pendingKind: isPending ? variables : null,
  };
}
